import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useSocket } from "../contexts/SocketContext"

// livePromise: run an async job and update its toast pending message from socket events
// Usage example:
// const { run, pendingMsg } = useLivePromise()
// run({
//   job: () => apiCall(...),
//   subscribeEvent: "job_progress", // server emits progress on this event
//   doneEvent: "job_done",          // optional: mark done when event arrives (or rely on job resolve)
//   errorEvent: "job_error",        // optional: show error when event arrives
//   correlationId: jobId,            // optional: only handle events with matching id
//   initialPending: "درحال ارسال درخواست به سرور",
// })
const useLivePromise = () => {
    const [pendingMsg, setPendingMsg] = useState("درحال ارسال درخواست به سرور")
    const toastIdRef = useRef(null)
    const currentCorrelationId = useRef(null)
    const timeoutRef = useRef(null)
    const { subscribe, unsubscribe } = useSocket()

    const updatePending = (msg) => {
        setPendingMsg(msg)
        if (toastIdRef.current) {
            console.log("Setting Progress")
            toast.update(toastIdRef.current, { render: msg })
        }
    }

    const progressHandler = (payload) => {
        // If server includes id, filter by correlationId
        if (payload) updatePending(payload)
    }

    const doneHandler = (payload) => {
        if (!currentCorrelationId.current || payload?.id === currentCorrelationId.current) {
            // Clear timeout since operation completed
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }

            if (toastIdRef.current)
                toast.update(toastIdRef.current, {
                    render: payload || "انجام شد",
                    type: "success",
                    isLoading: false,
                    autoClose: 2000,
                })
        }
    }

    const errorHandler = (payload) => {
        if (!currentCorrelationId.current || payload?.id === currentCorrelationId.current) {
            // Clear timeout since operation completed (with error)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }

            const errMsg = payload || "خطا در انجام عملیات"
            if (toastIdRef.current)
                toast.update(toastIdRef.current, {
                    render: errMsg,
                    type: "error",
                    isLoading: false,
                    autoClose: 4000,
                })
        }
    }

    const run = ({ job, subscribeEvent, doneEvent, errorEvent, correlationId, initialPending }) => {
        // Clean up any existing subscriptions and timeout first
        if (currentCorrelationId.current) {
            if (subscribeEvent) unsubscribe(subscribeEvent, progressHandler)
            if (doneEvent) unsubscribe(doneEvent, doneHandler)
            if (errorEvent) unsubscribe(errorEvent, errorHandler)
        }

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }

        // Update current correlation ID
        currentCorrelationId.current = correlationId

        // Set initial pending message
        setPendingMsg(initialPending || "درحال ارسال درخواست به سرور")

        // Subscribe to new events
        if (subscribeEvent) subscribe(subscribeEvent, progressHandler)
        if (doneEvent) subscribe(doneEvent, doneHandler)
        if (errorEvent) subscribe(errorEvent, errorHandler)

        // Run the job with a toast that we can update
        const promise = (async () => {
            try {
                // Create toast after job starts
                toastIdRef.current = toast.loading(pendingMsg)

                const result = await (typeof job === "function" ? job() : job)
                return result
            } catch (err) {
                throw err
            }
        })()

        promise
            .then((res) => {
                // Clear timeout since operation completed
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                    timeoutRef.current = null
                }

                if (toastIdRef.current) {
                    toast.update(toastIdRef.current, {
                        render: "با موفقیت انجام شد",
                        type: "success",
                        isLoading: false,
                        autoClose: 2000,
                    })
                }
                return res
            })
            .catch((err) => {
                // Clear timeout since operation completed (with error)
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current)
                    timeoutRef.current = null
                }

                if (toastIdRef.current) {
                    toast.update(toastIdRef.current, {
                        render: err?.message || "Operation failed",
                        type: "error",
                        isLoading: false,
                        autoClose: 4000,
                    })
                }
            })

        return promise
    }

    // Cleanup subscriptions when component unmounts
    useEffect(() => {
        return () => {
            if (currentCorrelationId.current) {
                // Clean up any remaining subscriptions
                if (currentCorrelationId.current) {
                    // We don't have the event names stored, so we can't unsubscribe
                    // The socket context will handle cleanup when it unmounts
                }
            }

            // Clear any remaining timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [])

    return { run, pendingMsg }
}

export default useLivePromise
